import React, { useEffect, useState } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import useAuth from '../../../Hooks/useAuth';
import useRolePermissions from '../../../Hooks/useRolePermissions';
import AdminLayout from '../../../components/AdminLayout/AdminLayout';
import DashboardMainContainer from '../../../components/common/Containers/DashboardMainContainer';
import ConfigurationListPaginationFooter from '../../../components/AdminDashboard/Configuration/ConfigurationListPaginationFooter';
import { ACTION_POPUP_CONTENT_WIDTH_CLASS } from '../../../components/popup/popupLayoutSpec';
import { useInlineRowActions, ROW_ACTIONS_MENU_HEIGHT } from '../../../Hooks/useInlineRowActions';
import { useConfigurationListPagination } from '../../../Hooks/useConfigurationListPagination';
import {
  AdminPageTitle,
  AdminPanelTitle,
  AdminHeaderAction,
  AdminTableHeader,
  AdminTableBody,
  AdminTableMeta,
  AdminStatusBadge,
  AdminRowMenuItem,
  AdminRowActionsButton,
  AdminFixedRowActionsMenu,
  AdminSupportingText,
  AdminDetailFieldLabel,
  AdminDetailFieldValue,
  AdminFormSecondaryAction,
  AdminFormFieldError,
  adminFormDetailInputClasses,
  adminFormTwoColGridClass,
  AdminRowMenuIcon,
  ADMIN_PAGE_TITLE_CLASS,
  ADMIN_TABLE_CLASS,
  ADMIN_FORM_PANEL_CLASS,
  ADMIN_FORM_FOOTER_CLASS,
} from '../shared/adminUx';
import {
  APPROVED_WORK_COUNTRIES,
  buildSampleTeamMembers,
  getActorDisplayName,
  isTeamOwnerActor,
  canViewTeam,
  nextTeamMemberId,
  TEAM_MEMBER_ADD_SUCCESS,
  memberHasValidStoredAccess,
} from '../../../data/teamConfig';
import {
  ACTIVATE_BLOCKED_NO_ACCESS,
  ACTIVATE_NOTICE,
  applyActivate,
  applyDeactivate,
  applyPasswordReset,
  collectMemberSaveValidationErrors,
  createPendingAccessMember,
  DEACTIVATE_NOTICE,
  getMemberJobRoleDisplay,
  getMemberPermissionModuleNames,
  getMemberPermissionsAccessDisplay,
  getMemberPlatformRoleDisplay,
  getMemberTeamDisplay,
  getTeamListActions,
  isActivateEligible,
  isDeactivateEligible,
  isProtectedMember,
  isResetPasswordEligible,
  LIST_ACTION_LABELS,
  RESET_PASSWORD_NOTICE,
} from '../../../data/teamWorkflow';
import { matchesUrlName, slugifyName } from '../../../utils/slugify';
import { PERMISSIONS_ACCESS_ROUTE } from '../../../data/permissionsConfig';

const memberPathSlug = (member) => encodeURIComponent(slugifyName(member?.name));

const formatMemberDateTime = (dateString) => {
  if (!dateString) return '—';
  const date = new Date(dateString);
  if (Number.isNaN(date.getTime())) return '—';
  const day = String(date.getDate()).padStart(2, '0');
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const year = date.getFullYear();
  const time = date
    .toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true })
    .replace(/\s/g, ' ');
  return `${day}/${month}/${year}, ${time}`;
};

const getEmptyMemberForm = () => ({
  firstName: '',
  lastName: '',
  workEmail: '',
  mobileNumber: '',
  approvedWorkCountry: '',
});

const listGridCols =
  'minmax(72px, 0.55fr) minmax(96px, 0.85fr) minmax(128px, 1.15fr) minmax(96px, 0.85fr) minmax(104px, 0.95fr) minmax(96px, 0.75fr) minmax(160px, 1.5fr) minmax(104px, 0.85fr) minmax(118px, 0.95fr) minmax(118px, 0.95fr) minmax(40px, 0.22fr)';

const PopupShell = ({ title, onClose, children, footer }) => (
  <div
    className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[9999] px-4"
    onClick={onClose}
    role="presentation"
  >
    <div
      className={`bg-white rounded-xl shadow-lg ${ACTION_POPUP_CONTENT_WIDTH_CLASS} max-h-[90vh] flex flex-col`}
      onClick={(e) => e.stopPropagation()}
      role="dialog"
      aria-modal="true"
    >
      <div className="relative px-5 pt-5 pb-3 border-b border-lightgray shrink-0">
        <AdminPanelTitle className="text-offblack text-center">{title}</AdminPanelTitle>
        <button
          type="button"
          onClick={onClose}
          className="absolute right-4 top-4 text-offblack hover:text-primaryblue"
          aria-label="Close"
        >
          <i className="fi fi-rs-cross text-sm" aria-hidden />
        </button>
      </div>
      <div className="px-5 py-4 overflow-y-auto flex-1 min-h-0">{children}</div>
      {footer ? <div className="px-5 pb-5 shrink-0">{footer}</div> : null}
    </div>
  </div>
);

const LIST_PREVIEW_COUNT = 3;

const ModulesCell = ({ names, fallbackLabel, onViewAll }) => {
  if (!names || names.length === 0) {
    const label = fallbackLabel || '—';
    return (
      <AdminTableBody className="truncate block" title={label}>
        {label}
      </AdminTableBody>
    );
  }

  const preview = names.slice(0, LIST_PREVIEW_COUNT).join(', ');
  const fullText = names.join(', ');

  if (names.length <= LIST_PREVIEW_COUNT) {
    return (
      <AdminTableBody className="block min-w-0 break-words" title={fullText}>
        {preview}
      </AdminTableBody>
    );
  }

  return (
    <AdminTableBody className="block min-w-0 w-full" title={fullText}>
      <span className="inline-flex min-w-0 max-w-full items-baseline">
        <span className="min-w-0 overflow-hidden whitespace-nowrap [text-overflow:clip]">
          {preview}
        </span>{' '}
        <button
          type="button"
          className="text-primaryblue hover:underline shrink-0 -ml-1 bg-white relative z-[1] pl-1"
          onClick={(e) => {
            e.stopPropagation();
            onViewAll?.();
          }}
        >
          View All
        </button>
      </span>
    </AdminTableBody>
  );
};

const TeamMembers = () => {
  const navigate = useNavigate();
  const { memberName } = useParams();
  const location = useLocation();
  const { auth } = useAuth();
  const { canViewModule, isFullAccess } = useRolePermissions();
  const actorName = getActorDisplayName(auth);
  const canManageTeam = isTeamOwnerActor(auth);
  const hasTeamMembersViewGrant = isFullAccess || canViewModule('team_members');
  const canViewTeamMembers = canViewTeam(auth, { hasTeamMembersViewGrant });

  const [members, setMembers] = useState(buildSampleTeamMembers);
  const [formErrors, setFormErrors] = useState({});
  const [memberForm, setMemberForm] = useState(getEmptyMemberForm);
  const [actionError, setActionError] = useState('');
  const [listFeedback, setListFeedback] = useState(location.state?.feedback || '');
  const [tempPasswordModal, setTempPasswordModal] = useState(null);
  const [copyDone, setCopyDone] = useState(false);

  const pathNorm = (location.pathname || '/').replace(/\/+$/, '') || '/';
  const isAddPath = pathNorm.endsWith('/team-members/new');
  const isResetPasswordView = /\/reset-password$/i.test(pathNorm);
  const isActivateView = /\/activate$/i.test(pathNorm);
  const isDeactivateView = /\/deactivate$/i.test(pathNorm);
  const resolvedMemberName = isAddPath ? 'new' : memberName;

  const selectedMember =
    resolvedMemberName && resolvedMemberName !== 'new'
      ? members.find((m) => matchesUrlName(m.name, resolvedMemberName))
      : null;
  const isAddMemberView = resolvedMemberName === 'new';
  const isEditView =
    Boolean(resolvedMemberName) &&
    resolvedMemberName !== 'new' &&
    /\/edit$/i.test(pathNorm);
  const isActionView = isResetPasswordView || isActivateView || isDeactivateView;
  const isDetailView =
    Boolean(resolvedMemberName) &&
    resolvedMemberName !== 'new' &&
    !isEditView &&
    !isActionView;
  const isView = isDetailView;

  const {
    rowActionsOpen,
    rowActionsRefs,
    tableScrollRef,
    rowActionsMenuLayout,
    rowActionsMenuRef,
    rowActionsMenuPlacementReady,
    toggleRowActions,
    closeRowActions,
  } = useInlineRowActions(ROW_ACTIONS_MENU_HEIGHT.QUADRUPLE);

  const {
    currentPage,
    setCurrentPage,
    totalPages,
    paginatedItems: paginatedMembers,
    rangeStart,
    rangeEnd,
    totalCount,
    showPagination,
  } = useConfigurationListPagination(members);

  useEffect(() => {
    if (location.state?.feedback) {
      setListFeedback(location.state.feedback);
      navigate(location.pathname, { replace: true, state: {} });
    }
  }, [location.state, location.pathname, navigate]);

  useEffect(() => {
    if (!canViewTeamMembers) return;
    if (!canManageTeam && (isAddMemberView || isEditView || isActionView)) {
      navigate(
        isAddMemberView
          ? '/admin/settings/team-members'
          : `/admin/settings/team-members/${resolvedMemberName}`,
        { replace: true },
      );
    }
  }, [
    canViewTeamMembers,
    canManageTeam,
    isAddMemberView,
    isEditView,
    isActionView,
    navigate,
    resolvedMemberName,
  ]);

  useEffect(() => {
    if (isAddMemberView) {
      setMemberForm(getEmptyMemberForm());
      setFormErrors({});
      setActionError('');
      return;
    }
    if (selectedMember && (isEditView || isDetailView)) {
      setMemberForm({
        firstName: selectedMember.firstName ?? '',
        lastName: selectedMember.lastName ?? '',
        workEmail: selectedMember.workEmail ?? '',
        mobileNumber: selectedMember.mobileNumber ?? '',
        approvedWorkCountry: selectedMember.approvedWorkCountry ?? '',
      });
      setFormErrors({});
      setActionError('');
    }
  }, [resolvedMemberName, isAddMemberView, isEditView, isDetailView, selectedMember]);

  useEffect(() => {
    if (isEditView && selectedMember && isProtectedMember(selectedMember)) {
      navigate(`/admin/settings/team-members/${memberPathSlug(selectedMember)}`, { replace: true });
    }
  }, [isEditView, selectedMember, navigate]);

  useEffect(() => {
    if (!selectedMember || !isActionView) return;
    if (isProtectedMember(selectedMember)) {
      navigate(`/admin/settings/team-members/${memberPathSlug(selectedMember)}`, { replace: true });
      return;
    }
    if (isResetPasswordView && !isResetPasswordEligible(selectedMember)) {
      navigate(`/admin/settings/team-members/${memberPathSlug(selectedMember)}`, { replace: true });
    }
    if (isActivateView && !isActivateEligible(selectedMember)) {
      navigate(`/admin/settings/team-members/${memberPathSlug(selectedMember)}`, { replace: true });
    }
    if (isDeactivateView && !isDeactivateEligible(selectedMember)) {
      navigate(`/admin/settings/team-members/${memberPathSlug(selectedMember)}`, { replace: true });
    }
  }, [
    selectedMember,
    isActionView,
    isResetPasswordView,
    isActivateView,
    isDeactivateView,
    navigate,
  ]);

  const touchAudit = (memberPatch = {}) => ({
    ...memberPatch,
    lastUpdated: new Date().toISOString(),
    lastUpdatedBy: actorName,
  });

  const handleMemberFieldChange = (field, value) => {
    setMemberForm((prev) => ({ ...prev, [field]: value }));
    setFormErrors((prev) => ({ ...prev, [field]: undefined }));
  };

  const validateMemberForm = ({ excludeMemberId = null } = {}) => {
    const errors = collectMemberSaveValidationErrors(memberForm, {
      members,
      excludeMemberId,
    });
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleAddMemberSave = () => {
    if (!canManageTeam) return;
    if (!validateMemberForm()) return;
    const draft = createPendingAccessMember(memberForm, { actorName });
    const newMember = {
      ...draft,
      teamMemberId: nextTeamMemberId(members),
    };
    setMembers((prev) => [...prev, newMember]);
    navigate('/admin/settings/team-members', {
      state: { feedback: TEAM_MEMBER_ADD_SUCCESS },
    });
  };

  const handleEditMemberSave = () => {
    if (!canManageTeam || !selectedMember) return;
    if (!validateMemberForm({ excludeMemberId: selectedMember.id })) return;

    const firstName = memberForm.firstName.trim();
    const lastName = memberForm.lastName.trim();
    const updatedName = `${firstName} ${lastName}`.trim();
    const nextEmail = memberForm.workEmail.trim();
    const nextMobile = memberForm.mobileNumber.trim();
    const nextCountry = memberForm.approvedWorkCountry.trim();
    const sensitiveChanged =
      nextEmail !== (selectedMember.workEmail || '') ||
      nextMobile !== (selectedMember.mobileNumber || '') ||
      nextCountry !== (selectedMember.approvedWorkCountry || '');

    setMembers((prev) =>
      prev.map((m) =>
        m.id === selectedMember.id
          ? touchAudit({
              ...m,
              firstName,
              lastName,
              name: updatedName,
              workEmail: nextEmail,
              mobileNumber: nextMobile,
              approvedWorkCountry: nextCountry,
              ...(sensitiveChanged
                ? {
                    sessionsRevokedAt: new Date().toISOString(),
                    mobileVerificationRequired:
                      nextMobile !== (selectedMember.mobileNumber || ''),
                  }
                : {}),
            })
          : m,
      ),
    );
    navigate(`/admin/settings/team-members/${encodeURIComponent(slugifyName(updatedName))}`);
  };

  const handleResetPasswordSave = () => {
    if (!canManageTeam || !selectedMember || !isResetPasswordEligible(selectedMember)) return;
    const { member: updated, temporaryPassword } = applyPasswordReset(selectedMember, {
      actorName,
    });
    setMembers((prev) => prev.map((m) => (m.id === selectedMember.id ? updated : m)));
    setCopyDone(false);
    setTempPasswordModal({ member: updated, temporaryPassword });
  };

  const handleActivateSave = () => {
    if (!canManageTeam || !selectedMember || !isActivateEligible(selectedMember)) return;
    if (!memberHasValidStoredAccess(selectedMember)) {
      setActionError(ACTIVATE_BLOCKED_NO_ACCESS);
      return;
    }
    const result = applyActivate(selectedMember, { actorName });
    if (result.blocked) {
      setActionError(result.reason || ACTIVATE_BLOCKED_NO_ACCESS);
      return;
    }
    setMembers((prev) => prev.map((m) => (m.id === selectedMember.id ? result.member : m)));
    navigate('/admin/settings/team-members');
  };

  const handleDeactivateSave = () => {
    if (!canManageTeam || !selectedMember || !isDeactivateEligible(selectedMember)) return;
    setMembers((prev) =>
      prev.map((m) => (m.id === selectedMember.id ? applyDeactivate(selectedMember, { actorName }) : m)),
    );
    navigate('/admin/settings/team-members');
  };

  const handleCopyPassword = async () => {
    if (!tempPasswordModal?.temporaryPassword) return;
    try {
      await navigator.clipboard.writeText(tempPasswordModal.temporaryPassword);
      setCopyDone(true);
    } catch {
      setCopyDone(false);
    }
  };

  const openMemberView = (member) => {
    navigate(`/admin/settings/team-members/${memberPathSlug(member)}`);
  };

  const cancelToList = () => navigate('/admin/settings/team-members');
  const cancelToMember = () => {
    if (selectedMember) {
      navigate(`/admin/settings/team-members/${memberPathSlug(selectedMember)}`);
      return;
    }
    cancelToList();
  };

  const handleRowAction = (action, member) => {
    closeRowActions();
    if (action === LIST_ACTION_LABELS.view) {
      openMemberView(member);
      return;
    }
    if (action === LIST_ACTION_LABELS.edit) {
      navigate(`/admin/settings/team-members/${memberPathSlug(member)}/edit`);
      return;
    }
    if (action === LIST_ACTION_LABELS.resetPassword) {
      navigate(`/admin/settings/team-members/${memberPathSlug(member)}/reset-password`);
      return;
    }
    if (action === LIST_ACTION_LABELS.deactivate) {
      navigate(`/admin/settings/team-members/${memberPathSlug(member)}/deactivate`);
      return;
    }
    if (action === LIST_ACTION_LABELS.activate) {
      navigate(`/admin/settings/team-members/${memberPathSlug(member)}/activate`);
    }
  };

  const renderFormField = (field, label, placeholder, type = 'text', readOnly = false) => (
    <div className="space-y-1 w-full min-w-0">
      <AdminDetailFieldLabel>
        {label} {!readOnly && <span className="text-alertred">*</span>}
      </AdminDetailFieldLabel>
      <input
        type={type}
        value={memberForm[field] || ''}
        onChange={(e) => handleMemberFieldChange(field, e.target.value)}
        placeholder={placeholder}
        readOnly={readOnly}
        className={adminFormDetailInputClasses(readOnly, Boolean(formErrors[field]))}
      />
      {formErrors[field] && <AdminFormFieldError>{formErrors[field]}</AdminFormFieldError>}
    </div>
  );

  const renderCountryField = (readOnly = false) => (
    <div className="space-y-1 w-full min-w-0">
      <AdminDetailFieldLabel>
        Approved Work Country {!readOnly && <span className="text-alertred">*</span>}
      </AdminDetailFieldLabel>
      {readOnly ? (
        <AdminDetailFieldValue className="py-2">
          {memberForm.approvedWorkCountry || '—'}
        </AdminDetailFieldValue>
      ) : (
        <select
          name="approvedWorkCountry"
          value={memberForm.approvedWorkCountry || ''}
          onChange={(e) => handleMemberFieldChange('approvedWorkCountry', e.target.value)}
          className={adminFormDetailInputClasses(false, Boolean(formErrors.approvedWorkCountry))}
        >
          <option value="">Select Approved Work Country</option>
          {APPROVED_WORK_COUNTRIES.map((country) => (
            <option key={country.value} value={country.value}>
              {country.label}
            </option>
          ))}
        </select>
      )}
      {formErrors.approvedWorkCountry && (
        <AdminFormFieldError>{formErrors.approvedWorkCountry}</AdminFormFieldError>
      )}
    </div>
  );

  const renderReadOnlyField = (label, value) => (
    <div className="space-y-1 w-full min-w-0">
      <AdminDetailFieldLabel>{label}</AdminDetailFieldLabel>
      <AdminDetailFieldValue className="py-2 break-words">{value || '—'}</AdminDetailFieldValue>
    </div>
  );

  const renderFormFooter = ({ onCancel, onSave, saveLabel = 'Save' }) => (
    <div className={ADMIN_FORM_FOOTER_CLASS}>
      <div className="flex flex-wrap gap-3 items-center justify-end">
        <AdminFormSecondaryAction type="button" onClick={onCancel}>
          Cancel
        </AdminFormSecondaryAction>
        <AdminHeaderAction type="button" onClick={onSave}>
          {saveLabel}
        </AdminHeaderAction>
      </div>
    </div>
  );

  const renderTempPasswordPopup = () => {
    if (!tempPasswordModal) return null;
    return (
      <PopupShell
        title="Temporary Password"
        onClose={() => {
          setTempPasswordModal(null);
          navigate('/admin/settings/team-members');
        }}
        footer={
          <div className="flex flex-wrap justify-center gap-3">
            <AdminFormSecondaryAction type="button" onClick={handleCopyPassword}>
              {copyDone ? 'Copied' : 'Copy Password'}
            </AdminFormSecondaryAction>
            <AdminHeaderAction
              type="button"
              onClick={() => {
                setTempPasswordModal(null);
                navigate('/admin/settings/team-members');
              }}
            >
              Done
            </AdminHeaderAction>
          </div>
        }
      >
        <div className="space-y-3 text-center">
          <AdminSupportingText className="text-offblack">
            Give this one-time temporary password to{' '}
            {tempPasswordModal.member?.name || 'the Team Member'}. It expires after 24 hours if
            unused and is shown once.
          </AdminSupportingText>
          <p className="text-offblack text-sm font-mono break-all py-2 px-3 bg-pastalBlueWhite rounded-xl">
            {tempPasswordModal.temporaryPassword}
          </p>
        </div>
      </PopupShell>
    );
  };

  const renderActionScreen = () => {
    if (!selectedMember) {
      return (
        <AdminLayout>
          <AdminSupportingText className="text-mediumgray">Member not found.</AdminSupportingText>
        </AdminLayout>
      );
    }

    const pageTitle = isResetPasswordView
      ? 'Reset Team Member Password'
      : isActivateView
        ? 'Activate Team Member'
        : 'Deactivate Team Member';

    const notice = isResetPasswordView
      ? RESET_PASSWORD_NOTICE
      : isActivateView
        ? ACTIVATE_NOTICE
        : DEACTIVATE_NOTICE;

    const onSave = isResetPasswordView
      ? handleResetPasswordSave
      : isActivateView
        ? handleActivateSave
        : handleDeactivateSave;

    return (
      <AdminLayout>
        <div
          className={`flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4 w-full min-w-0 ${ADMIN_PAGE_TITLE_CLASS}`}
        >
          <AdminPageTitle className="!mb-0">{pageTitle}</AdminPageTitle>
        </div>

        <DashboardMainContainer className={ADMIN_FORM_PANEL_CLASS}>
          <div className="space-y-4 w-full">
            <div className={adminFormTwoColGridClass}>
              {renderReadOnlyField('Member ID', selectedMember.teamMemberId)}
              {renderReadOnlyField('Team Member', selectedMember.name)}
              {renderReadOnlyField('Work Email', selectedMember.workEmail)}
              {renderReadOnlyField(
                isActivateView || isDeactivateView ? 'Current Status' : 'Status',
                selectedMember.status,
              )}
            </div>
            <AdminSupportingText className="text-offblack">{notice}</AdminSupportingText>
            {actionError ? <AdminFormFieldError>{actionError}</AdminFormFieldError> : null}
          </div>
        </DashboardMainContainer>

        {renderFormFooter({ onCancel: cancelToList, onSave, saveLabel: 'Save' })}
        {renderTempPasswordPopup()}
      </AdminLayout>
    );
  };

  const renderMemberScreen = (mode) => {
    const isAdd = mode === 'add';
    const isEdit = mode === 'edit';

    if (!isAdd && !selectedMember) {
      return (
        <AdminLayout>
          <AdminSupportingText className="text-mediumgray">Member not found.</AdminSupportingText>
        </AdminLayout>
      );
    }

    const pageTitle = isAdd
      ? 'Add Team Member'
      : isEdit
        ? 'Edit Team Member'
        : 'Team Member Detail';

    const moduleNames = selectedMember ? getMemberPermissionModuleNames(selectedMember) : [];

    return (
      <AdminLayout>
        <div
          className={`flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4 w-full min-w-0 ${ADMIN_PAGE_TITLE_CLASS}`}
        >
          <AdminPageTitle className="!mb-0">{pageTitle}</AdminPageTitle>
        </div>

        <form
          onSubmit={(e) => {
            e.preventDefault();
            if (isAdd) handleAddMemberSave();
            else if (isEdit) handleEditMemberSave();
          }}
          className={`space-y-4${isView ? ` ${ADMIN_PAGE_TITLE_CLASS}` : ''}`}
        >
          {isView ? (
            <>
              <DashboardMainContainer className="!bg-white w-full">
                <div className="space-y-4 w-full">
                  <AdminPanelTitle className="text-offblack mb-4">
                    Team Member Information
                  </AdminPanelTitle>
                  <div className={adminFormTwoColGridClass}>
                    {selectedMember?.teamMemberId
                      ? renderReadOnlyField('Member ID', selectedMember.teamMemberId)
                      : null}
                    {renderReadOnlyField('First Name', selectedMember.firstName)}
                    {renderReadOnlyField('Last Name', selectedMember.lastName)}
                    {renderReadOnlyField('Work Email', selectedMember.workEmail)}
                    {renderReadOnlyField('Mobile Number', selectedMember.mobileNumber)}
                    {renderReadOnlyField(
                      'Approved Work Country',
                      selectedMember.approvedWorkCountry,
                    )}
                  </div>
                </div>
              </DashboardMainContainer>

              <DashboardMainContainer className="!bg-white w-full">
                <div className="space-y-4 w-full">
                  <AdminPanelTitle className="text-offblack mb-4">
                    Assignment & Access
                  </AdminPanelTitle>
                  <div className={adminFormTwoColGridClass}>
                    {renderReadOnlyField('Team', getMemberTeamDisplay(selectedMember))}
                    {renderReadOnlyField('Job Role', getMemberJobRoleDisplay(selectedMember))}
                    {renderReadOnlyField(
                      'Platform Role',
                      getMemberPlatformRoleDisplay(selectedMember),
                    )}
                    {renderReadOnlyField('Status', selectedMember.status)}
                  </div>
                  <div className="space-y-1 w-full min-w-0">
                    <AdminDetailFieldLabel>Permissions & Access</AdminDetailFieldLabel>
                    {selectedMember.isOwner || selectedMember.isCoOwner ? (
                      <AdminDetailFieldValue className="py-2 break-words">
                        {getMemberPermissionsAccessDisplay(selectedMember)}
                      </AdminDetailFieldValue>
                    ) : moduleNames.length === 0 ? (
                      <AdminDetailFieldValue className="py-2 break-words">
                        Not Assigned
                      </AdminDetailFieldValue>
                    ) : (
                      <ul className="divide-y divide-lightgray border-y border-lightgray mt-1">
                        {moduleNames.map((name) => (
                          <li key={name} className="py-2.5 break-words">
                            <AdminTableBody>{name}</AdminTableBody>
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>
                </div>
              </DashboardMainContainer>

              <DashboardMainContainer className="!bg-white w-full">
                <div className="space-y-4 w-full">
                  <AdminPanelTitle className="text-offblack mb-4">Record Information</AdminPanelTitle>
                  <div className={adminFormTwoColGridClass}>
                    {renderReadOnlyField(
                      'Member Since',
                      formatMemberDateTime(selectedMember.memberSince),
                    )}
                    {renderReadOnlyField(
                      'Last Updated',
                      formatMemberDateTime(selectedMember.lastUpdated),
                    )}
                  </div>
                </div>
              </DashboardMainContainer>
            </>
          ) : (
            <DashboardMainContainer className={ADMIN_FORM_PANEL_CLASS}>
              <div className="space-y-4">
                {isEdit && selectedMember?.teamMemberId
                  ? renderReadOnlyField('Member ID', selectedMember.teamMemberId)
                  : null}
                {renderFormField('firstName', 'First Name', 'Enter First Name')}
                {renderFormField('lastName', 'Last Name', 'Enter Last Name')}
                {renderFormField('workEmail', 'Work Email', 'Enter Work Email', 'email')}
                {renderFormField(
                  'mobileNumber',
                  'Mobile Number',
                  'Enter Mobile Number with Country Code',
                  'tel',
                )}
                {renderCountryField()}
              </div>
            </DashboardMainContainer>
          )}
        </form>

        {!isView &&
          renderFormFooter({
            onCancel: isAdd ? cancelToList : cancelToMember,
            onSave: isAdd ? handleAddMemberSave : handleEditMemberSave,
            saveLabel: 'Save',
          })}
      </AdminLayout>
    );
  };

  if (!canViewTeamMembers) {
    return (
      <AdminLayout>
        <AdminPageTitle>Team Members</AdminPageTitle>
        <AdminSupportingText className="text-mediumgray mt-4">
          You do not have permission to access Team Members. Platform Owner and Co-owner may view
          this module. Eligible Team Members with a Team Members View grant may inspect records within
          their permitted scope. Only the Platform Owner can manage Team Members.
        </AdminSupportingText>
      </AdminLayout>
    );
  }

  if (isAddMemberView) return renderMemberScreen('add');
  if (isActionView) return renderActionScreen();
  if (isDetailView) return renderMemberScreen('view');
  if (isEditView) return renderMemberScreen('edit');

  return (
    <AdminLayout>
      <div className="min-w-0">
        <div
          className={`flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4 ${ADMIN_PAGE_TITLE_CLASS}`}
        >
          <AdminPageTitle className="!mb-0">Team Members</AdminPageTitle>
          {canManageTeam && (
            <AdminHeaderAction onClick={() => navigate('/admin/settings/team-members/new')}>
              Add Team Member
            </AdminHeaderAction>
          )}
        </div>

        {listFeedback ? (
          <AdminSupportingText className="text-offblack mb-4">{listFeedback}</AdminSupportingText>
        ) : null}

        <DashboardMainContainer className={`${ADMIN_TABLE_CLASS} max-w-full min-w-0`}>
          <div ref={tableScrollRef} className="overflow-x-auto">
            <div
              className="grid w-full py-3 text-offblack border-b border-lightgray px-4 gap-x-3 gap-y-2"
              style={{ gridTemplateColumns: listGridCols }}
            >
              <div className="flex items-center min-w-0">
                <AdminTableHeader className="truncate">Member ID</AdminTableHeader>
              </div>
              <div className="flex items-center min-w-0">
                <AdminTableHeader className="truncate">Team Member</AdminTableHeader>
              </div>
              <div className="flex items-center min-w-0">
                <AdminTableHeader className="truncate">Work Email</AdminTableHeader>
              </div>
              <div className="flex items-center min-w-0">
                <AdminTableHeader className="truncate">Team</AdminTableHeader>
              </div>
              <div className="flex items-center min-w-0">
                <AdminTableHeader className="truncate">Job Role</AdminTableHeader>
              </div>
              <div className="flex items-center min-w-0">
                <AdminTableHeader className="truncate">Platform Role</AdminTableHeader>
              </div>
              <div className="flex items-center min-w-0">
                <AdminTableHeader className="truncate">Permissions & Access</AdminTableHeader>
              </div>
              <div className="flex items-center min-w-0 shrink-0">
                <AdminTableHeader>Status</AdminTableHeader>
              </div>
              <div className="flex items-center min-w-0">
                <AdminTableHeader className="truncate">Member Since</AdminTableHeader>
              </div>
              <div className="flex items-center min-w-0">
                <AdminTableHeader className="truncate">Last Updated</AdminTableHeader>
              </div>
              <div className="flex items-center shrink-0 justify-center">
                <AdminTableHeader>Actions</AdminTableHeader>
              </div>
            </div>

            {paginatedMembers.length === 0 ? (
              <div className="py-10 px-4 text-center">
                <AdminSupportingText className="text-mediumgray">
                  No team members yet.
                </AdminSupportingText>
              </div>
            ) : (
              paginatedMembers.map((member) => {
                const rowActions = getTeamListActions(member, { canManage: canManageTeam });
                const teamLabel = getMemberTeamDisplay(member);
                const jobRoleLabel = getMemberJobRoleDisplay(member);
                const platformRole = getMemberPlatformRoleDisplay(member);
                const accessLabel = getMemberPermissionsAccessDisplay(member);
                const moduleNames = getMemberPermissionModuleNames(member);
                const permissionsFallback =
                  member.isOwner || member.isCoOwner ? accessLabel : accessLabel;
                return (
                  <div
                    key={member.id}
                    role="button"
                    tabIndex={0}
                    onClick={() => openMemberView(member)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        openMemberView(member);
                      }
                    }}
                    className="grid w-full py-3 px-4 text-offblack border-b border-lightgray last:border-b-0 gap-x-3 gap-y-2 cursor-pointer rounded-sm outline-none focus-visible:ring-2 focus-visible:ring-primaryblue focus-visible:ring-offset-1"
                    style={{ gridTemplateColumns: listGridCols }}
                  >
                    <div className="flex items-center min-w-0 overflow-hidden">
                      <AdminTableMeta className="truncate block" title={member.teamMemberId}>
                        {member.teamMemberId}
                      </AdminTableMeta>
                    </div>
                    <div className="flex items-center min-w-0 overflow-hidden">
                      <AdminTableBody emphasis className="truncate block" title={member.name}>
                        {member.name}
                      </AdminTableBody>
                    </div>
                    <div className="flex items-center min-w-0 overflow-hidden">
                      <AdminTableBody className="truncate block" title={member.workEmail}>
                        {member.workEmail}
                      </AdminTableBody>
                    </div>
                    <div className="flex items-center min-w-0 overflow-hidden">
                      <AdminTableBody className="truncate block" title={teamLabel}>
                        {teamLabel}
                      </AdminTableBody>
                    </div>
                    <div className="flex items-center min-w-0 overflow-hidden">
                      <AdminTableBody className="truncate block" title={jobRoleLabel}>
                        {jobRoleLabel}
                      </AdminTableBody>
                    </div>
                    <div className="flex items-center min-w-0 overflow-hidden">
                      <AdminTableBody className="truncate block" title={platformRole}>
                        {platformRole}
                      </AdminTableBody>
                    </div>
                    <div className="flex items-center min-w-0 overflow-hidden">
                      <ModulesCell
                        names={moduleNames}
                        fallbackLabel={permissionsFallback}
                        onViewAll={() =>
                          navigate(
                            `${PERMISSIONS_ACCESS_ROUTE}/member/${memberPathSlug(member)}`,
                            {
                              state: {
                                member,
                                members,
                                from: '/admin/settings/team-members',
                              },
                            },
                          )
                        }
                      />
                    </div>
                    <div className="flex items-center min-w-0 shrink-0">
                      <AdminStatusBadge status={member.status} label={member.status} />
                    </div>
                    <div className="flex items-center min-w-0 overflow-hidden">
                      <AdminTableMeta className="truncate block whitespace-nowrap">
                        {formatMemberDateTime(member.memberSince)}
                      </AdminTableMeta>
                    </div>
                    <div className="flex items-center min-w-0 overflow-hidden">
                      <AdminTableMeta className="truncate block whitespace-nowrap">
                        {formatMemberDateTime(member.lastUpdated)}
                      </AdminTableMeta>
                    </div>
                    <div
                      className="flex items-center justify-center shrink-0 overflow-visible"
                      onClick={(e) => e.stopPropagation()}
                    >
                      <div
                        className="relative inline-flex"
                        ref={(el) => {
                          rowActionsRefs.current[member.id] = el;
                        }}
                      >
                        <AdminRowActionsButton
                          onClick={(e) => {
                            e.stopPropagation();
                            toggleRowActions(member.id);
                          }}
                          aria-expanded={!!rowActionsOpen[member.id]}
                          title="Actions"
                          aria-label="Row actions"
                        >
                          <AdminRowMenuIcon />
                        </AdminRowActionsButton>
                        {rowActionsOpen[member.id] && rowActionsMenuLayout && (
                          <AdminFixedRowActionsMenu
                            layout={rowActionsMenuLayout}
                            menuRef={rowActionsMenuRef}
                            placementReady={rowActionsMenuPlacementReady}
                          >
                            {rowActions.map((action) => (
                              <AdminRowMenuItem
                                key={action}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleRowAction(action, member);
                                }}
                              >
                                {action}
                              </AdminRowMenuItem>
                            ))}
                          </AdminFixedRowActionsMenu>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })
            )}
          </div>
        </DashboardMainContainer>

        <ConfigurationListPaginationFooter
          show={showPagination}
          rangeStart={rangeStart}
          rangeEnd={rangeEnd}
          totalCount={totalCount}
          currentPage={currentPage}
          totalPages={totalPages}
          onPrev={() => setCurrentPage((prev) => Math.max(1, prev - 1))}
          onNext={() => setCurrentPage((prev) => Math.min(totalPages, prev + 1))}
          goToPage={setCurrentPage}
        />
      </div>
    </AdminLayout>
  );
};

export default TeamMembers;
